/**
 * TriageQueue — the tenant-level inbox triage queue: classify-routed inbound
 * emails that could not be auto-routed (/v1/inbound-emails?status=
 * pending_triage), mirroring HRTickets' list → drawer → action mechanics.
 * The action footer is a DEPARTMENT PICKER + discard, not a status lifecycle:
 * resolve dispatches the chosen department's skill run server-side and flips
 * the row to 'routed'; discard is a pure status flip.
 *
 * The four resolve outcomes are handled distinctly (ApiError.status):
 *   200 → routed — splice the row out of the pending list, close, toast
 *   400 → the picked department went stale — refetch departments, row stays
 *   404 → already handled elsewhere — refresh the list, no local splice
 *   409 → the department's skill requires a PDF this email cannot supply —
 *         the backend kept the row pending_triage (do NOT splice); surfaced
 *         as an inline "handle manually or discard" notice in the drawer
 *
 * DEFERRED (needs DTO support — do not build yet): proactively flagging
 * document-skill departments as unroutable for attachment-less emails needs
 * TenantDepartment to expose the skill's input requirements, which the wire
 * does not carry today. The 409 handler above is the reliable backstop.
 */
import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, CornerUpRight, Trash2, Paperclip, ShieldAlert } from 'lucide-react'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { usePolledAsync, fetchInboundEmails, fetchDepartments, resolveInboundEmail, discardInboundEmail } from '../../api'
import type { InboundEmail, TenantDepartment } from '../../types'
import { ApiError, apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import {
  attention, confidence as confidenceScale, ink, neutral, overlay,
  statusInboundEmail,
} from '../../../../theme/tokens'

// triageReason → short row label + full drawer framing. The reason strings
// mix the classifier-emitted set (ambiguous / sensitiveBoundary / noFit —
// camelCase, the __route guest contract) with the host-side set (snake_case,
// loop.go + ingest). An unmapped reason renders raw rather than hiding.
const REASON_ROW: Record<string, string> = {
  classifier_triage: 'Classifier uncertain',
  ambiguous: 'Ambiguous match',
  sensitiveBoundary: 'Sensitive boundary',
  noFit: 'No clear match',
  stale_department: 'Stale department',
  no_active_departments: 'No departments',
  skill_input_unsatisfiable: 'Needs manual routing',
}

const REASON_FRAMING: Record<string, string> = {
  classifier_triage: 'Classifier was uncertain which department',
  ambiguous: 'Two departments matched closely',
  sensitiveBoundary: 'A sensitive department was a close match — routed to review',
  noFit: 'No department clearly matched',
  stale_department: 'The matched department was deactivated',
  no_active_departments: 'No active departments configured',
  skill_input_unsatisfiable: "Confident match, but the email couldn't be auto-processed",
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ')
}

// Status = colored dot + label. Unknown statuses fall back to the neutral dot.
function EmailStatus({ status }: { status: string }) {
  const cfg = statusInboundEmail[status as keyof typeof statusInboundEmail] ?? { dot: neutral.dot, label: ink.secondary }
  return (
    <span className="inline-flex items-center gap-2 text-12 font-medium capitalize whitespace-nowrap"
      style={{ color: cfg.label }}>
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cfg.dot }} />
      {statusLabel(status)}
    </span>
  )
}

// bodyText is the FULL body (HRTicket got a pre-truncated bodyPreview; this
// wire does not) — truncate client-side for the row.
const PREVIEW_CHARS = 120
function previewOf(bodyText?: string): string {
  if (!bodyText) return ''
  const oneLine = bodyText.replace(/\s+/g, ' ').trim()
  return oneLine.length > PREVIEW_CHARS ? `${oneLine.slice(0, PREVIEW_CHARS)}…` : oneLine
}

function fmtBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function fmtReceived(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

// Row-grid date slot is 56px — day + month only (the drawer shows the full stamp).
function fmtReceivedShort(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

// HRTickets' confidence meter: sage ≥ 85 (the platform's safe-accept
// threshold), ochre below.
function ConfidenceMeter({ value, barWidthClass = 'w-16' }: { value: number; barWidthClass?: string }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className={`inline-block ${barWidthClass} h-1 rounded-full overflow-hidden`}
        style={{ background: confidenceScale.track }}>
        <span className="block h-full rounded-full"
          style={{
            width: `${Math.max(0, Math.min(100, value))}%`,
            background: value >= 85 ? confidenceScale.high : confidenceScale.low,
          }} />
      </span>
      <span className="text-12 font-semibold text-ink-primary">{Math.round(value)}</span>
    </span>
  )
}

// ── Detail drawer ───────────────────────────────────────────────────────────────

function TriageDrawer({ email, departments, onClose, onResolve, onDiscard, busy, unroutableMsg }: {
  email: InboundEmail
  departments: TenantDepartment[]
  onClose: () => void
  onResolve: (id: string, departmentId: string) => void
  onDiscard: (id: string) => void
  busy: boolean
  /** The 409 outcome — the row stayed pending; shown inline until close/retry. */
  unroutableMsg: string | null
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  // The picker pre-selects the classifier's confident choice when present (the
  // skill_input_unsatisfiable case), resyncing if the drawer swaps emails.
  const [pick, setPick] = useState<string>(email.resolvedDepartmentId ?? '')
  useEffect(() => { setPick(email.resolvedDepartmentId ?? '') }, [email.id, email.resolvedDepartmentId])

  const picked = departments.find(d => d.id === pick)
  const suggested = email.resolvedDepartmentId
    ? departments.find(d => d.id === email.resolvedDepartmentId)
    : undefined
  const confidence = typeof email.classifierConfidence === 'number' ? email.classifierConfidence : null
  const atts = email.attachments ?? []
  const framing = email.triageReason ? (REASON_FRAMING[email.triageReason] ?? email.triageReason) : null

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(560px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div className="min-w-0">
            <div className="text-detail-title font-bold text-ink-primary truncate">{email.subject || '(no subject)'}</div>
            <div className="text-12 text-ink-muted">{email.senderEmail || 'Unknown sender'}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        <div className="flex items-center gap-3 px-6 py-4 border-b border-edge-row flex-shrink-0 flex-wrap">
          <EmailStatus status={email.status} />
          {email.triageReason && (
            <span className="inline-flex items-center px-2 py-1 rounded text-12 text-ink-secondary bg-surface-panel">
              {REASON_ROW[email.triageReason] ?? email.triageReason}
            </span>
          )}
          {confidence !== null && (
            <span className="inline-flex items-center gap-2 text-12 text-ink-secondary ml-auto whitespace-nowrap">
              AI confidence <ConfidenceMeter value={confidence} />
            </span>
          )}
        </div>

        {/* Reason-aware framing: why this email is here + what to do next. */}
        <div className="px-6 pt-4 flex-shrink-0">
          <div className="px-3 py-2 rounded-control text-12 bg-surface-panel border border-edge-panel">
            {framing && <div className="text-ink-secondary">{framing}</div>}
            <div className="font-semibold text-ink-primary">
              {email.resolvedDepartmentId
                ? `We're confident this is ${suggested?.name ?? 'the suggested department'} but couldn't auto-extract it`
                : 'Which department should handle this?'}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
          <div>
            <div className="k-eyebrow uppercase mb-1">Message</div>
            {email.bodyText ? (
              <p className="text-13 text-ink-body leading-relaxed whitespace-pre-wrap">{email.bodyText}</p>
            ) : (
              <p className="text-12 text-ink-muted">No message body.</p>
            )}
          </div>

          {atts.length > 0 && (
            <div>
              <div className="k-eyebrow uppercase mb-1">Attachments</div>
              <div className="flex flex-col gap-1">
                {atts.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-control bg-surface-panel text-12">
                    <Paperclip size={12} className="text-ink-faint flex-shrink-0" />
                    <span className="text-ink-body truncate flex-1">{a.fileName || '(unnamed)'}</span>
                    <span className="text-ink-muted flex-shrink-0">{a.mimeType || '—'}</span>
                    <span className="text-ink-faint flex-shrink-0">{fmtBytes(a.sizeBytes)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-13">
            <div><div className="text-11 text-ink-muted">Mailbox</div><div className="text-ink-primary break-all">{email.recipientAddress || '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Received</div><div className="text-ink-primary">{fmtReceived(email.createdAt)}</div></div>
          </div>
        </div>

        {/* Action footer — department picker + route/discard (NOT a status select). */}
        <div className="flex flex-col gap-3 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          {unroutableMsg && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-control text-12"
              style={{
                background: attention['banner-bg'],
                border: `1px solid ${attention['banner-border']}`,
                color: attention['banner-text'],
              }}>
              <ShieldAlert size={12} className="flex-shrink-0" />
              {unroutableMsg}
            </div>
          )}
          {picked?.isSensitive && (
            <div className="flex items-center gap-2 text-12" style={{ color: attention['text-strong'] }}>
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: attention.indicator }} />
              Sensitive department — this email will be routed into a discretion-handled queue.
            </div>
          )}
          <div className="flex items-center gap-2">
            <select value={pick} onChange={e => setPick(e.target.value)}
              className="flex-1 min-w-0 h-10 px-3 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body focus:outline-none focus:border-accent">
              <option value="">Choose a department…</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}{d.isSensitive ? ' — sensitive' : ''}</option>
              ))}
            </select>
            <button onClick={() => pick && onResolve(email.id, pick)} disabled={busy || !pick}
              className="k-btn k-btn--lg inline-flex items-center gap-2 whitespace-nowrap flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed">
              <CornerUpRight size={14} /> {busy ? 'Working…' : `Route${picked ? ` to ${picked.name}` : ''}`}
            </button>
            <button onClick={() => onDiscard(email.id)} disabled={busy}
              className="k-btn-secondary k-btn-secondary--flex whitespace-nowrap flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed">
              <Trash2 size={14} /> Discard
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function TriageQueue() {
  const { data, loading, error, refresh } = usePolledAsync(() => fetchInboundEmails(), 10_000, [])
  const [emails, setEmails] = useState<InboundEmail[]>([])
  const [departments, setDepartments] = useState<TenantDepartment[]>([])
  const [detail, setDetail] = useState<InboundEmail | null>(null)
  const [busy, setBusy] = useState(false)
  const [unroutableMsg, setUnroutableMsg] = useState<string | null>(null)

  useEffect(() => { if (data) setEmails(data) }, [data])

  // Departments load once (picker options + resolvedDepartmentId → name
  // lookup); refetched when a resolve 400s — the active list went stale.
  const loadDepartments = useCallback(() => {
    fetchDepartments()
      .then(setDepartments)
      .catch(() => toast.error('Failed to load departments'))
  }, [])
  useEffect(() => { loadDepartments() }, [loadDepartments])

  async function handleResolve(id: string, departmentId: string) {
    setBusy(true)
    setUnroutableMsg(null)
    const dept = departments.find(d => d.id === departmentId)
    try {
      await resolveInboundEmail(id, departmentId)
      setEmails(prev => prev.filter(e => e.id !== id))
      setDetail(null)
      toast.success(`Routed to ${dept?.name ?? 'department'}`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        // Stale department: our picker options are out of date; the row stays.
        toast.error('That department is no longer active')
        loadDepartments()
      } else if (e instanceof ApiError && e.status === 404) {
        // No longer pending — someone/something already handled it; let the
        // refresh reconcile the list rather than splicing locally.
        toast('This email was already handled')
        refresh()
      } else if (e instanceof ApiError && e.status === 409) {
        // skill_input_unsatisfiable, human edition: the department's skill
        // requires a PDF this email cannot supply. The backend kept the row
        // pending_triage — do NOT splice it out.
        setUnroutableMsg("Couldn't auto-extract — this email needs a PDF the system can't rebuild. Handle manually or discard.")
      } else {
        toast.error(apiErrorMessage(e, 'Resolve failed'))
      }
    } finally {
      setBusy(false)
    }
  }

  async function handleDiscard(id: string) {
    setBusy(true)
    try {
      await discardInboundEmail(id)
      setEmails(prev => prev.filter(e => e.id !== id))
      setDetail(null)
      setUnroutableMsg(null)
      toast.success('Discarded')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Discard failed'))
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  // Newest first: the department is usually unknown pre-resolve, so there is
  // no sensitive-first tier to sort by here.
  const sorted = [...emails].sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Triage"
        subtitle={`${sorted.length} email${sorted.length !== 1 ? 's' : ''} waiting for review`}
      />

      <div className="k-card">
        {sorted.length === 0 ? (
          <EmptyState
            message="No emails waiting for review"
            hint="Every inbound email routed cleanly — an empty triage queue is a good thing."
          />
        ) : (
          /* Row grid: [dot][subject + truncated body preview][reason 150][confidence 88][date 56] */
          <div>
            {sorted.map(e => {
              const conf = typeof e.classifierConfidence === 'number' ? e.classifierConfidence : null
              const dotCfg = statusInboundEmail[e.status as keyof typeof statusInboundEmail] ?? { dot: neutral.dot }
              return (
                <div key={e.id} onClick={() => { setUnroutableMsg(null); setDetail(e) }}
                  className="flex items-center gap-4 px-5 py-3 border-b border-edge-row-faint last:border-b-0 cursor-pointer hover:bg-surface-row-hover transition-colors">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotCfg.dot }} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-13 font-medium text-ink-primary truncate">{e.subject || '(no subject)'}</span>
                    {e.bodyText && (
                      <span className="block text-12 text-ink-muted truncate">{previewOf(e.bodyText)}</span>
                    )}
                  </span>
                  <span className="w-[150px] flex-shrink-0 text-12 text-ink-secondary truncate">
                    {e.triageReason ? (REASON_ROW[e.triageReason] ?? e.triageReason) : '—'}
                  </span>
                  <span className="w-[88px] flex-shrink-0">
                    {conf !== null
                      ? <ConfidenceMeter value={conf} barWidthClass="w-10" />
                      : <span className="text-12 text-ink-faint">—</span>}
                  </span>
                  <span className="w-14 flex-shrink-0 text-right text-12 text-ink-faint whitespace-nowrap">{fmtReceivedShort(e.createdAt)}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {detail && (
        <TriageDrawer
          email={detail}
          departments={departments}
          onClose={() => { setDetail(null); setUnroutableMsg(null) }}
          onResolve={handleResolve}
          onDiscard={handleDiscard}
          busy={busy}
          unroutableMsg={unroutableMsg}
        />
      )}
    </div>
  )
}
